import { format, formatISO, isSameDay, isSameWeek, parseISO } from 'date-fns'
import {
  instanceOf,
  func,
  number,
  object,
  oneOf,
  string,
  shape,
  oneOfType,
} from 'prop-types'
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { useResizeDetector } from 'react-resize-detector'
import VirtualList from 'react-tiny-virtual-list-oss'
import { Timescale } from '@opallabs/concepts'
import { kuiperClassNames } from '@opallabs/kuiper'
import {
  dateFromDayIndex,
  dateFromWeekIndex,
  getDayIndexFromDate,
  getWeekIndexFromDate,
  startOfWeekByDay,
} from '../utils/timeFunctions'
import { WeekViewMode } from '../WeekView/types'
import { useWeekViewSettings } from '../WeekView/WeekViewSettingsContext'
import Day from './Day'
import {
  getNearestDateWithData,
  isScrollingBack,
  DAYCOUNT,
  DAYFETCHBUFFER,
  DAYWIDTH,
  WEEKCOUNT,
  WEEKFETCHBUFFER,
  WEEKWIDTH,
} from './helpers'
import { actionTypes, reducer } from './reducer'
import { useBreakdown } from './useBreakdown'
import Week from './Week'

import './styles.scss'

const Histogram = ({
  content,
  contentCountForCurrentWeek,
  currentBrandId,
  draggedMomentParams,
  fetchHistogramData,
  focusedDay,
  mode,
  moments,
  momentCountForCurrentWeek,
  onFocusedDayChange,
  onHistogramDataChange,
  onNavigation,
  onRescheduleContent,
  onUpdateMoment,
  searchTerm,
  selectedFilters,
  story,
  weekStartsOn,
}) => {
  const { selectedSort, spanInMarketDays, viewMode } = useWeekViewSettings()
  const isContentMode = viewMode === WeekViewMode.Content

  // Active-week breakdown — drives the stacked segments and tooltip on the
  // 7 bars currently being rendered in the grid. Other histogram bars stay
  // solid via the `isFocused`/`isInActiveWeek` gates downstream.
  const breakdown = useBreakdown({
    content: isContentMode ? content : undefined,
    moments,
    focusedDay,
    weekStartsOn,
    selectedSort,
    currentBrandId,
    spanInMarketDays,
  })
  const indexGetterFromDate =
    mode === Timescale.Day ? getDayIndexFromDate : getWeekIndexFromDate
  const UNITCOUNT = mode === Timescale.Day ? DAYCOUNT : WEEKCOUNT
  const UNITWIDTH = mode === Timescale.Day ? DAYWIDTH : WEEKWIDTH
  const UNITFETCHBUFFER =
    mode === Timescale.Day ? DAYFETCHBUFFER : WEEKFETCHBUFFER
  const TODAY = useMemo(() => new Date(), [])
  const [state, dispatch] = useReducer(reducer, {
    activeIndex: indexGetterFromDate(focusedDay, UNITCOUNT),
    dragOffset: 0,
    filterScope: '',
    firstFetchMarker: null,
    firstVisibleIndex: indexGetterFromDate(focusedDay, UNITCOUNT),
    focusedDay,
    height: 0,
    histogramData: {},
    launchingData: {},
    largestDayCount: 1,
    lastFetchMarker: null,
    lastVisibleIndex: indexGetterFromDate(focusedDay, UNITCOUNT) + 1,
    scrollOffset: 0,
    searchTerm,
    momentCountForCurrentWeek,
    spanInMarketDays,
    viewMode,
    width: document.body.clientWidth,
  })

  useEffect(() => {
    dispatch({
      type: actionTypes.SET_ACTIVE_INDEX,
      payload: { focusedDay, mode },
    })
  }, [focusedDay, mode, state.height])

  // In Day mode the strip marks the first day at or after the one asked for
  // that has content, so the day on show is not always the day requested.
  // Tell the shell each time that mark moves, so its label says what is on
  // screen. Only a move is reported: reporting every difference would undo
  // the next/previous buttons, which count off the shell's own day.
  const reportedDay = useRef(focusedDay)
  useEffect(() => {
    if (mode !== Timescale.Day) return
    if (isSameDay(reportedDay.current, state.focusedDay)) return
    reportedDay.current = state.focusedDay
    onFocusedDayChange?.(state.focusedDay)
  }, [mode, onFocusedDayChange, state.focusedDay])

  useEffect(() => {
    onHistogramDataChange?.(state.histogramData)
  }, [onHistogramDataChange, state.histogramData])

  // Skip a refetch when the resolved request params match the last one
  // we initiated — useEffect deps below land asynchronously and can
  // fire the callback multiple times with identical payloads.
  const lastFetchKeyRef = useRef(null)

  const onFetchHistogramData = useCallback(() => {
    if (!state.firstFetchMarker || !state.lastFetchMarker) return null
    const dateGetterFromIndex =
      mode === Timescale.Day ? dateFromDayIndex : dateFromWeekIndex

    const startDateFromIndex = dateGetterFromIndex(
      UNITCOUNT,
      state.firstFetchMarker,
      new Date()
    )
    const endDateFromIndex = dateGetterFromIndex(
      UNITCOUNT,
      state.lastFetchMarker,
      new Date()
    )
    const startDateToFetch = format(startDateFromIndex, 'yyyy-MM-dd')
    const endDateToFetch = format(endDateFromIndex, 'yyyy-MM-dd')

    // BrandSetting filters are applied server-side in
    // `Moments::Moment.filter_moments`, so the wire payload doesn't
    // carry them. `filterScope` and `spanInMarketDays` still feed the
    // reducer's reset gate — when either changes, the server response
    // covers a different scope and the merged state must drop prior
    // bars instead of layering on top.
    const filterScope = selectedFilters ? JSON.stringify(selectedFilters) : ''

    // The active-week count is part of the key so an in-week create or delete
    // (which changes it) triggers a refetch and refreshes the bar height. A
    // reschedule leaves the count unchanged and patches the bars optimistically.
    const activeWeekCount = isContentMode
      ? contentCountForCurrentWeek
      : momentCountForCurrentWeek
    const fetchKey = [
      startDateToFetch,
      endDateToFetch,
      searchTerm ?? '',
      String(spanInMarketDays),
      filterScope,
      viewMode,
      String(currentBrandId),
      String(activeWeekCount ?? 0),
    ].join('|')
    if (fetchKey === lastFetchKeyRef.current) return null
    lastFetchKeyRef.current = fetchKey

    return fetchHistogramData(startDateToFetch, endDateToFetch, searchTerm, {
      spanInMarketDays,
    }).then((histogramData) =>
      dispatch({
        type: actionTypes.SET_HISTOGRAM_DATA,
        payload: {
          histogramData,
          mode,
          searchTerm,
          viewMode,
          filterScope,
          spanInMarketDays,
        },
      })
    )
  }, [
    fetchHistogramData,
    mode,
    searchTerm,
    selectedFilters,
    state.firstFetchMarker,
    state.lastFetchMarker,
    spanInMarketDays,
    UNITCOUNT,
    viewMode,
    currentBrandId,
    isContentMode,
    contentCountForCurrentWeek,
    momentCountForCurrentWeek,
  ])

  // In Content mode the bars represent posts; refetch whenever the
  // wormhole content slice's reference changes so create + WeekView
  // grid reschedule refresh the bars. The Histogram bar drop path
  // keeps its instant optimistic patch via `rescheduleContent`.
  const contentSliceDep = isContentMode ? content : null
  useEffect(() => {
    onFetchHistogramData()
  }, [
    onFetchHistogramData,
    contentCountForCurrentWeek,
    momentCountForCurrentWeek,
    selectedFilters,
    contentSliceDep,
    viewMode,
  ])

  const currentStory =
    story || (draggedMomentParams && draggedMomentParams.primaryStory)

  const setDragEnd = useCallback(() => {
    dispatch({ type: actionTypes.SET_DRAG_END })
  }, [])

  const setDragStart = useCallback((e) => {
    dispatch({ type: actionTypes.SET_DRAG_START, payload: e.clientX })
  }, [])

  const updateHistogramData = useCallback((fromDate, toDate) => {
    dispatch({
      type: actionTypes.UPDATE_HISTOGRAM_DATA,
      payload: { fromDate, toDate },
    })
  }, [])

  const updateMoment = useCallback(
    (params) => {
      const movedFrom = parseISO(params.momentOriginalDate)
      const movedTo = parseISO(params.update.scheduled_at)
      const hasMovedDays = !isSameDay(movedFrom, movedTo)

      if (hasMovedDays) {
        updateHistogramData(
          formatISO(movedFrom, { representation: 'date' }),
          formatISO(movedTo, { representation: 'date' })
        )
      }

      onUpdateMoment(params)
    },
    [onUpdateMoment, updateHistogramData]
  )

  // Mirror of `updateMoment` for content drops. The Day variants pass
  // `previousScheduledAt` so we can shift the bar counts optimistically
  // — a within-week move doesn't change any refetch-trigger count, so
  // without this the source/destination bars stay stale until refresh.
  const rescheduleContent = useCallback(
    ({ previousScheduledAt, ...params }) => {
      if (previousScheduledAt && params.scheduled_at) {
        const from = formatISO(parseISO(previousScheduledAt), {
          representation: 'date',
        })
        const to = formatISO(parseISO(params.scheduled_at), {
          representation: 'date',
        })
        if (from !== to) updateHistogramData(from, to)
      }
      onRescheduleContent?.(params)
    },
    [onRescheduleContent, updateHistogramData]
  )

  const setDragOffset = useCallback(
    (e) => {
      if (!state.isDragging) return
      dispatch({
        type: actionTypes.SET_DRAG_OFFSET,
        payload: { offset: e.clientX, mode },
      })
    },
    [mode, state.isDragging]
  )

  const changeActiveDay = useCallback(
    (index, date) => {
      const shouldPreventNavigationWhileDragging =
        state.scrollOffset !== state.previousScrollOffset
      if (shouldPreventNavigationWhileDragging) return

      let destinationDate
      if (mode === Timescale.Day) {
        destinationDate = getNearestDateWithData(state.histogramData, date)
      }

      if (mode === Timescale.Week) {
        destinationDate = startOfWeekByDay(
          UNITCOUNT,
          index,
          TODAY,
          weekStartsOn
        )
      }

      if (isSameDay(destinationDate, focusedDay)) {
        dispatch({
          type: actionTypes.SET_ACTIVE_INDEX,
          payload: { focusedDay, mode },
        })
      }

      onNavigation(destinationDate)
    },
    [
      focusedDay,
      mode,
      onNavigation,
      state.histogramData,
      state.previousScrollOffset,
      state.scrollOffset,
      UNITCOUNT,
      weekStartsOn,
      TODAY,
    ]
  )

  const onResize = useCallback(
    (width, height) => {
      // Avoid setting before histogram is initialized
      if (!width || !height) {
        return
      }

      dispatch({
        type: actionTypes.SET_DIMENSIONS,
        payload: { height, width, mode },
      })
    },
    [mode]
  )

  const { ref } = useResizeDetector({
    handleHeight: true,
    handleWidth: true,
    refreshMode: 'debounce',
    refreshRate: 250,
    onResize,
  })

  const setFetchRange = useCallback(
    (firstFetchMarker, lastFetchMarker) => {
      dispatch({
        type: actionTypes.SET_FETCH_MARKERS,
        payload: { firstFetchMarker, lastFetchMarker, mode, weekStartsOn },
      })
    },
    [mode, weekStartsOn]
  )

  const onUpdateOffset = useCallback(
    (offset) => {
      const shouldSetBackwardRange =
        isScrollingBack(offset, state.scrollOffset) &&
        state.firstVisibleIndex - state.firstFetchMarker < 2
      const shouldSetForwardRange =
        !isScrollingBack(offset, state.scrollOffset) &&
        state.lastFetchMarker - state.lastVisibleIndex < 2

      if (shouldSetBackwardRange || shouldSetForwardRange) {
        const firstFetchMarker = state.firstVisibleIndex - UNITFETCHBUFFER
        const lastFetchMarker = state.lastVisibleIndex + UNITFETCHBUFFER
        setFetchRange(firstFetchMarker, lastFetchMarker)
      }
    },
    [
      setFetchRange,
      state.firstFetchMarker,
      state.firstVisibleIndex,
      state.lastFetchMarker,
      state.lastVisibleIndex,
      state.scrollOffset,
      UNITFETCHBUFFER,
    ]
  )

  const updateOffset = useCallback(
    (offset) => {
      if (state.firstVisibleIndex === 0 && isScrollingBack(offset)) return
      if (state.lastVisibleIndex === UNITCOUNT - 1 && !isScrollingBack(offset))
        return
      onUpdateOffset(offset)
      dispatch({
        type: actionTypes.SET_SCROLL_OFFSET,
        payload: { offset, mode },
      })
    },
    [
      mode,
      onUpdateOffset,
      state.firstVisibleIndex,
      state.lastVisibleIndex,
      UNITCOUNT,
    ]
  )

  const onScroll = useCallback(
    (offset) => {
      updateOffset(offset)
    },
    [updateOffset]
  )

  // trackpads on Macs trigger onWheel when scrolling
  // up and down, and onScroll when scrolling left
  // and right. This causes both of the callbacks
  // to fire depending on your movement. A solution
  // could be to determine which one is the intention
  // via x/y diffs and prevent the other callback
  // from triggering.
  const onWheel = useCallback(
    (e) => {
      const offset = state.scrollOffset + e.deltaY
      updateOffset(offset)
    },
    [state.scrollOffset, updateOffset]
  )

  const handleClick = useCallback(
    (e) => {
      const hasDragged = state.scrollOffset !== state.previousScrollOffset
      if (hasDragged) {
        // this prevents changing weeks when the user
        // has dragged the histogram
        e.stopPropagation()
      }
      setDragEnd(e)
    },
    [setDragEnd, state.previousScrollOffset, state.scrollOffset]
  )

  const renderDay = useCallback(
    (style, dayIndex) => (
      <Day
        breakdown={breakdown}
        date={dateFromDayIndex(DAYCOUNT, dayIndex, TODAY)}
        draggedMomentParams={draggedMomentParams}
        focusedDay={state.focusedDay}
        histogramData={state.histogramData}
        isContentMode={isContentMode}
        largestDayCount={state.largestDayCount}
        mode={mode}
        onClick={(date) => changeActiveDay(dayIndex, date)}
        onRescheduleContent={rescheduleContent}
        onUpdateMoment={onUpdateMoment}
        story={currentStory}
        style={style}
      />
    ),
    [
      breakdown,
      changeActiveDay,
      currentStory,
      state.histogramData,
      draggedMomentParams,
      isContentMode,
      mode,
      onUpdateMoment,
      rescheduleContent,
      state.focusedDay,
      state.largestDayCount,
      TODAY,
    ]
  )

  const renderWeek = useCallback(
    (style, weekIndex) => {
      const firstDayOfWeek = startOfWeekByDay(
        UNITCOUNT,
        weekIndex,
        TODAY,
        weekStartsOn
      )
      const isFocused = isSameWeek(focusedDay, firstDayOfWeek)
      return (
        <Week
          breakdown={breakdown}
          histogramData={state.histogramData}
          launchingData={state.launchingData}
          isContentMode={isContentMode}
          draggedMomentParams={draggedMomentParams}
          firstDayOfWeek={firstDayOfWeek}
          focusedDay={focusedDay}
          isFocused={isFocused}
          weekIndex={weekIndex}
          largestDayCount={state.largestDayCount}
          onClick={() => changeActiveDay(weekIndex)}
          onRescheduleContent={rescheduleContent}
          onUpdateMoment={updateMoment}
          story={currentStory}
          style={style}
        />
      )
    },
    [
      changeActiveDay,
      currentStory,
      breakdown,
      state.histogramData,
      state.launchingData,
      draggedMomentParams,
      focusedDay,
      isContentMode,
      rescheduleContent,
      state.largestDayCount,
      updateMoment,
      TODAY,
      weekStartsOn,
      UNITCOUNT,
    ]
  )

  return (
    <div
      aria-hidden="true"
      className={kuiperClassNames('histogram', `mode-${mode}`, 'n10')}
      onMouseMove={setDragOffset}
      onMouseDown={setDragStart}
      onMouseLeave={() => {
        if (!state.isDragging) return
        setDragEnd()
      }}
      onClickCapture={handleClick}
      ref={ref}
    >
      <VirtualList
        width={state.width}
        height={state.height}
        itemCount={UNITCOUNT}
        itemSize={UNITWIDTH}
        scrollDirection="horizontal"
        scrollOffset={state.scrollOffset}
        scrollToIndex={story && indexGetterFromDate(focusedDay, UNITCOUNT)}
        scrollToAlignment="center"
        onScroll={onScroll}
        onWheel={onWheel}
        renderItem={({ index, style }) => (
          <Fragment key={index}>
            {mode === Timescale.Day && renderDay(style, index)}
            {mode === Timescale.Week && renderWeek(style, index)}
          </Fragment>
        )}
      />
    </div>
  )
}

Histogram.propTypes = {
  content: object,
  contentCountForCurrentWeek: number,
  currentBrandId: oneOfType([string, number]),
  draggedMomentParams: shape({
    id: string.isRequired,
    scheduledAt: oneOfType([instanceOf(Date), string]).isRequired,
    primaryStoryInterval: shape({
      start: instanceOf(Date).isRequired,
      end: instanceOf(Date).isRequired,
    }),
  }),
  fetchHistogramData: func.isRequired,
  focusedDay: instanceOf(Date).isRequired,
  mode: oneOf([Timescale.Day, Timescale.Month, Timescale.Week]),
  moments: object,
  momentCountForCurrentWeek: number,
  onFocusedDayChange: func,
  onHistogramDataChange: func,
  onNavigation: func.isRequired,
  onRescheduleContent: func,
  onUpdateMoment: func.isRequired,
  searchTerm: string,
  selectedFilters: object,
  story: object,
  weekStartsOn: number,
}

export default memo(Histogram)
